/**
 * RPC 服务处理函数签名
 * 每个 service handler（如 ui-service、theme-service）都实现此接口
 */
export type ServiceHandler = (
  pluginId: string,
  method: string,
  params: Record<string, unknown> | undefined
) => Promise<unknown>;

/**
 * RPC 消息路由器
 *
 * 将插件发来的 RPC 请求（如 "ui.showToast"）按 namespace 分发到对应的 ServiceHandler。
 * namespace 是方法名中第一个 "." 前的部分（如 "ui"），方法名是 "." 后的部分（如 "showToast"）。
 *
 * 注册示例：router.register("ui", uiServiceHandler)
 * 路由示例：router.route(pluginId, "ui.showToast", { message: "hello" })
 */
export class MessageRouter {
  /** namespace → handler 映射表 */
  private services = new Map<string, ServiceHandler>();

  /** 注册一个 namespace 对应的服务处理器 */
  register(namespace: string, handler: ServiceHandler): void {
    this.services.set(namespace, handler);
  }

  /**
   * 路由 RPC 请求：解析 "namespace.method" 格式，查找 handler 并调用
   * @throws 方法格式错误或 namespace 未注册时抛出带 code 的错误
   */
  async route(
    pluginId: string,
    fullMethod: string,
    params: Record<string, unknown> | undefined
  ): Promise<unknown> {
    const dotIndex = fullMethod.indexOf(".");
    if (dotIndex === -1) {
      throw Object.assign(new Error(`Invalid method format: ${fullMethod}`), { code: "INVALID_METHOD" });
    }

    const namespace = fullMethod.slice(0, dotIndex);
    const method = fullMethod.slice(dotIndex + 1);

    const handler = this.services.get(namespace);
    if (!handler) {
      throw Object.assign(new Error(`Unknown service: ${namespace}`), { code: "UNKNOWN_SERVICE" });
    }

    return handler(pluginId, method, params);
  }
}
